import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Button, Card, Image, Input, Radio, Space, Spin, Typography, message } from 'antd';
import { renderExperimentsApi } from '../services/catalogServiceAPI/renderExperimentsProxy';
import { SquareImage, loadRaterName, saveRaterName } from './shared';

const { Title, Text } = Typography;
const LETTERS = 'ABCDEFGHIJ';
const IMAGE_SIZE = 280;

const emptyScores = (candidates) => Object.fromEntries(candidates.map((c) => [c.candidateId, {}]));

const isComplete = (scores, candidates, criteria) =>
    candidates.every((c) => criteria.every((k) => scores[c.candidateId]?.[k.id] != null));

const scaleOptions = (criterion) =>
    Array.from({ length: criterion.max - criterion.min + 1 }, (_, i) => ({ value: criterion.min + i, label: String(criterion.min + i) }));

export function RenderExperimentRate() {
    const history = useHistory();
    const { id } = useParams();
    const [rater, setRater] = useState(loadRaterName());
    const [started, setStarted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [current, setCurrent] = useState(null);
    const [skipped, setSkipped] = useState([]);
    const [scores, setScores] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const fetchNext = useCallback(async () => {
        setLoading(true);
        try {
            const next = await renderExperimentsApi.next(id, rater, skipped);
            setCurrent(next);
            if (next.candidates) setScores(emptyScores(next.candidates));
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [id, rater, skipped]);

    useEffect(() => { if (started) fetchNext(); }, [started, fetchNext]);

    const begin = () => {
        const name = rater.trim();
        if (!name) return;
        saveRaterName(name);
        setRater(name);
        setStarted(true);
    };

    const criteria = useMemo(() => current?.criteria ?? [], [current]);
    const ready = useMemo(
        () => Boolean(current?.candidates) && isComplete(scores, current.candidates, criteria),
        [scores, current, criteria]
    );
    const setScore = (candidateId, criterionId, value) =>
        setScores((prev) => ({ ...prev, [candidateId]: { ...prev[candidateId], [criterionId]: value } }));

    const submit = async () => {
        setSubmitting(true);
        try {
            await renderExperimentsApi.vote(id, {
                sourceRenderingId: current.source.id,
                raterName: rater,
                scores,
            });
            await fetchNext();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Skipped sources stay unrated; the server steps past them for this session only.
    const skip = () => setSkipped((s) => [...s, current.source.id]);

    if (!started) {
        return (
            <div style={{ padding: 24, maxWidth: 520 }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={3} style={{ margin: 0 }}>Who is rating?</Title>
                    <Text type="secondary">Type your own name — not the account this computer is signed in with. Votes are grouped by this name.</Text>
                    <Input size="large" placeholder="Your name" value={rater} onChange={(e) => setRater(e.target.value)} onPressEnter={begin} autoFocus />
                    <Space>
                        <Button type="primary" size="large" disabled={!rater.trim()} onClick={begin}>Start rating</Button>
                        <Button size="large" onClick={() => history.push(`/experiments/${id}`)}>Back</Button>
                    </Space>
                </Space>
            </div>
        );
    }

    if (loading && !current) return <div style={{ padding: 24 }}><Spin /></div>;

    const progress = current?.progress;
    if (current?.done || !current?.source) {
        return (
            <div style={{ padding: 24, maxWidth: 640 }}>
                <Space direction="vertical" size="middle">
                    <Title level={3} style={{ margin: 0 }}>All done — thank you, {rater}!</Title>
                    <Text>You rated {progress?.rated ?? 0} of {progress?.total ?? 0} sources.</Text>
                    {progress?.pendingGeneration > 0 && (
                        <Alert type="info" showIcon message={`${progress.pendingGeneration} source(s) are still generating — come back later to rate them.`} />
                    )}
                    {skipped.length > 0 && <Text type="secondary">{skipped.length} skipped this session.</Text>}
                    <Space>
                        <Button type="primary" onClick={() => history.push(`/experiments/${id}/results`)}>See results</Button>
                        <Button onClick={() => { setSkipped([]); setStarted(false); }}>Rate as someone else</Button>
                    </Space>
                </Space>
            </div>
        );
    }

    const { source, candidates } = current;

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <Title level={4} style={{ margin: 0 }}>Source {progress.rated + 1} of {progress.total}</Title>
                        <Text type="secondary">Rating as <b>{rater}</b> · {source.timeOfDay} · {source.environment}. Score every candidate on each parameter — {criteria.map((k) => `${k.label} ${k.min}–${k.max}`).join(', ')}.</Text>
                    </div>
                    <Space>
                        <Button onClick={skip} disabled={submitting}>Skip</Button>
                        <Button type="primary" onClick={submit} loading={submitting} disabled={!ready}>Submit &amp; next</Button>
                    </Space>
                </div>

                <Image.PreviewGroup>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
                        <Card size="small" title="Original" style={{ width: IMAGE_SIZE + 2 }} bodyStyle={{ padding: 0 }}>
                            <SquareImage src={source.url} alt="Original 3D view" size={IMAGE_SIZE} />
                        </Card>
                        {candidates.map((c, index) => (
                            <Card key={c.candidateId} size="small" title={`Candidate ${LETTERS[index] ?? index + 1}`} style={{ width: IMAGE_SIZE + 2 }} bodyStyle={{ padding: 0 }}>
                                <SquareImage src={c.url} alt={`Candidate ${LETTERS[index] ?? index + 1}`} size={IMAGE_SIZE} />
                                <Space direction="vertical" size="small" style={{ padding: 12, width: '100%' }}>
                                    {criteria.map((k) => (
                                        <div key={k.id}>
                                            <Text style={{ fontSize: 12 }}>{k.label}</Text>
                                            <Radio.Group
                                                size="small"
                                                optionType="button"
                                                buttonStyle="solid"
                                                value={scores[c.candidateId]?.[k.id]}
                                                onChange={(e) => setScore(c.candidateId, k.id, e.target.value)}
                                                style={{ display: 'flex', width: '100%' }}
                                            >
                                                {scaleOptions(k).map((o) => (
                                                    <Radio.Button key={o.value} value={o.value} style={{ flex: 1, padding: 0, textAlign: 'center', minWidth: 0 }}>{o.label}</Radio.Button>
                                                ))}
                                            </Radio.Group>
                                        </div>
                                    ))}
                                </Space>
                            </Card>
                        ))}
                    </div>
                </Image.PreviewGroup>

                {!ready && <Text type="secondary">Score each candidate on every parameter to enable Submit. Click an image to zoom.</Text>}
            </Space>
        </div>
    );
}
